import type { ComponentType } from 'react';

import type { ViewMode } from '@/visualization/display/types';
import type { LayoutPreset } from '@/visualization/services/viewport-layout-service';
import { WINDOW_LEVEL_PRESETS } from '@/visualization/services/window-level-presets';
import type { WindowLevelPresetId } from '@/visualization/services/window-level-presets';

// ============================================================================
// Feature Visibility Types
// ============================================================================

/** Context passed to each visibility rule when evaluating. */
export interface IFeatureVisibilityContext {
    /** Currently active workflow step ID */
    readonly stepId: string;
    /** Check if a feature is currently registered in the display manager */
    hasFeature(featureId: string): boolean;
}

/** Declarative rule mapping a feature ID to its desired visibility. */
export interface IFeatureVisibilityRule {
    /** The feature ID to control */
    readonly featureId: string;
    /** Compute whether this feature should be visible given the current context.
     *  Return boolean for uniform visibility across all view modes,
     *  or a Map<ViewMode, boolean> for per-view-mode control. */
    isVisible(context: IFeatureVisibilityContext): boolean | ReadonlyMap<ViewMode, boolean>;
}

// ============================================================================
// Workflow Step Types
// ============================================================================

/** A step whose main view is a viewport layout (VTK viewports, panels, etc.) */
export interface ILayoutMainView {
    readonly type: 'layout';
    /** Must reference a layout name from `WorkflowConfig.layouts` */
    readonly layoutName: string;
}

/** A step whose main view is a component replacing the viewport area entirely */
export interface IComponentMainView {
    readonly type: 'component';
    readonly component: ComponentType;
}

export type WorkflowMainView = ILayoutMainView | IComponentMainView;

/** A workflow step: pairs a sidebar panel with a main view. */
export interface IWorkflowStep {
    /** Unique step identifier */
    readonly id: string;
    /** Display title shown in the sidebar tab */
    readonly title: string;
    /** Sidebar content rendered when this step is active */
    readonly sidebar: ComponentType;
    /** What to show in the main area */
    readonly mainView: WorkflowMainView;
}

// ============================================================================
// Workflow Config
// ============================================================================

export interface WorkflowConfig {
    readonly workflowId: string;

    /** Ordered layout presets available in this workflow */
    readonly layouts: readonly LayoutPreset[];

    /** Panel components: panelId -> React component */
    readonly panels: Readonly<Record<string, ComponentType>>;

    /** Workflow steps defining sidebar panels and their main views */
    readonly steps: readonly IWorkflowStep[];

    /** ID of the step active when entering the viewer */
    readonly defaultStepId: string;

    /** Optional header extra slot (e.g., save status for EVAR) */
    readonly headerExtra?: ComponentType<{ caseId: string }>;

    /**
     * Declarative feature visibility rules.
     * Applied automatically by `useWorkflowFeatureVisibility` in ViewerContent.
     * Rules are re-evaluated on step change and on FEATURE_ADDED/FEATURE_REMOVED events.
     */
    readonly featureVisibility?: readonly IFeatureVisibilityRule[];

    /**
     * Optional preset id (from WINDOW_LEVEL_PRESETS) applied by "Reset to defaults".
     * When omitted, reset falls back to the hardcoded 800/200 default — non-breaking.
     */
    readonly defaultWindowLevelPresetId?: WindowLevelPresetId;
}

/**
 * Create and validate a WorkflowConfig at definition time.
 *
 * Ensures:
 * - `layouts` is non-empty
 * - `steps` is non-empty
 * - `defaultStepId` matches one of the step IDs
 * - Every step with a layout mainView references a valid layout name
 * - Every `panelId` referenced in layouts has a corresponding entry in `panels`
 */
export function createWorkflowConfig(config: WorkflowConfig): WorkflowConfig {
    if (config.layouts.length === 0) {
        throw new Error(`[WorkflowConfig:${config.workflowId}] layouts must not be empty`);
    }

    if (config.steps.length === 0) {
        throw new Error(`[WorkflowConfig:${config.workflowId}] steps must not be empty`);
    }

    const stepIds = new Set(config.steps.map((s) => s.id));
    if (stepIds.size !== config.steps.length) {
        const seen = new Set<string>();
        const duplicates = config.steps
            .map((s) => s.id)
            .filter((id) => {
                if (seen.has(id)) return true;
                seen.add(id);
                return false;
            });
        throw new Error(`[WorkflowConfig:${config.workflowId}] duplicate step IDs: ${duplicates.join(', ')}`);
    }
    if (!stepIds.has(config.defaultStepId)) {
        throw new Error(
            `[WorkflowConfig:${config.workflowId}] defaultStepId "${config.defaultStepId}" not found in steps: ${[...stepIds].join(', ')}`
        );
    }

    const layoutNames = new Set(config.layouts.map((l) => l.name));

    for (const step of config.steps) {
        if (step.mainView.type === 'layout' && !layoutNames.has(step.mainView.layoutName)) {
            throw new Error(
                `[WorkflowConfig:${config.workflowId}] step "${step.id}" references layout "${step.mainView.layoutName}" not found in layouts: ${[...layoutNames].join(', ')}`
            );
        }
    }

    const panelKeys = new Set(Object.keys(config.panels));
    for (const layout of config.layouts) {
        for (const slot of layout.viewports) {
            if (slot.slotKind === 'panel') {
                const panelId = slot.panelId;
                if (!panelKeys.has(panelId)) {
                    throw new Error(
                        `[WorkflowConfig:${config.workflowId}] layout "${layout.name}" references panelId "${panelId}" not found in panels: ${[...panelKeys].join(', ')}`
                    );
                }
            }
        }
    }

    if (config.defaultWindowLevelPresetId !== undefined) {
        const ids = WINDOW_LEVEL_PRESETS.map((p) => p.id);
        if (!ids.includes(config.defaultWindowLevelPresetId)) {
            throw new Error(
                `[WorkflowConfig:${config.workflowId}] defaultWindowLevelPresetId "${config.defaultWindowLevelPresetId}" not found in WINDOW_LEVEL_PRESETS: ${ids.join(', ')}`
            );
        }
    }

    return config;
}
